/* eslint-disable no-unused-vars */
/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DetailedStatisticsCard from "examples/Cards/StatisticsCards/DetailedStatisticsCard";
import SalesTable from "examples/Tables/SalesTable";
import CategoriesList from "examples/Lists/CategoriesList";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { Card, Divider } from "@mui/material";

// Argon Dashboard 2 MUI base styles
import typography from "assets/theme/base/typography";

// Dashboard layout components
import Slider from "layouts/dashboard/components/Slider";
import StatCard from "layouts/dashboard/components/StatCard/StatCard";
import ClientAnalyticsCard from "layouts/dashboard/components/ClientAnalyticsCard";
import TutorAnalyticsCard from "layouts/dashboard/components/TutorAnalyticsCard";

// Data
import gradientLineChartData from "layouts/dashboard/data/gradientLineChartData";
import salesTableData from "layouts/dashboard/data/salesTableData";
import categoriesListData from "layouts/dashboard/data/categoriesListData";

// API
import api from "utils/api";
import { getFinancialComparison, getAllTutorPaymentCycles } from "services/financeService";
import { getAllTransactions } from "services/transactionService";

import { Link } from "react-router-dom";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const formatCurrency = (value = 0) => {
  const numericValue = Number(value);
  return currencyFormatter.format(Number.isFinite(numericValue) ? numericValue : 0);
};

function Default() {
  const { size } = typography;

  const [counts, setCounts] = useState({
    tutors: 0,
    parents: 0,
    students: 0,
    subjects: 0,
    categories: 0,
  });

  const [supportStats, setSupportStats] = useState({
    total: 0,
    open: 0,
    closed: 0,
    terminated: 0,
    onHold: 0,
  });

  const [financialComparison, setFinancialComparison] = useState({
    expected: { expense: 0, revenue: 0, grossMargin: 0 },
    current: { expense: 0, revenue: 0, grossMargin: 0 },
  });

  const [clientAnalytics, setClientAnalytics] = useState({
    recentClients: [],
    clientsCreated28Days: 0,
    clientsCreatedPrevious28Days: 0,
    enquiries28Days: 0,
    enquiriesPrevious28Days: 0,
  });

  const [tutorAnalytics, setTutorAnalytics] = useState({
    recentTutors: [],
    tutorsCreated28Days: 0,
    tutorsCreatedPrevious28Days: 0,
    interviewInvites28Days: 0,
    interviewInvitesPrevious28Days: 0,
  });

  const [paymentStats, setPaymentStats] = useState({
    pendingPayments: { count: 0, amount: 0 },
    expiredPackages: { count: 0, amount: 0 },
    expectedRecurringPayment: 0,
    expectedTutorSalary: 0,
  });

  const [leadStats, setLeadStats] = useState({
    prospect: 0,
    tutorFound: 0,
    demoFixed: 0,
    converted: 0,
    conversionRate: 0,
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch tutor counts by status
        let tutorCount = 0;
        try {
          const tutorResponse = await api.get("/tutor", {
            params: { page: 1, limit: 1 },
          });
          console.log("Tutor response:", tutorResponse.data);
          tutorCount =
            tutorResponse.data.data?.count ||
            tutorResponse.data.data?.pagination?.totalEntries ||
            0;
          console.log("Tutor count:", tutorCount);
        } catch (error) {
          console.error("Error fetching tutor count:", error);
        }

        // Fetch students to count unique parents
        let parentCount = 0;
        try {
          const studentsResponse = await api.get("/student");
          console.log("Students response for parents:", studentsResponse.data);
          const students = studentsResponse.data.data || [];
          const uniqueParents = new Set();
          students.forEach((student) => {
            if (student?.parentId && !student.parentId.isDeleted) {
              uniqueParents.add(student.parentId._id);
            }
          });
          parentCount = uniqueParents.size;
          console.log("Parent count:", parentCount);
        } catch (error) {
          console.error("Error fetching parent count:", error);
        }

        // Fetch students count
        let studentCount = 0;
        try {
          const studentsResponse = await api.get("/student");
          console.log("Students response for count:", studentsResponse.data);
          studentCount = studentsResponse.data.count || 0;
          console.log("Student count:", studentCount);
        } catch (error) {
          console.error("Error fetching student count:", error);
        }

        // Fetch subjects count
        let subjectCount = 0;
        try {
          const subjectsResponse = await api.get("/subjects");
          console.log("Subjects response:", subjectsResponse.data);
          subjectCount = subjectsResponse.data.count || 0;
          console.log("Subject count:", subjectCount);
        } catch (error) {
          console.error("Error fetching subject count:", error);
        }

        // Fetch categories count
        let categoryCount = 0;
        try {
          const categoriesResponse = await api.get("/categories");
          console.log("Categories response:", categoriesResponse.data);
          const categories = categoriesResponse.data.data || [];
          const activeCategories = categories.filter((categoryItem) => !categoryItem.isDeleted);
          categoryCount = activeCategories.length;
          console.log("Category count:", categoryCount);
        } catch (error) {
          console.error("Error fetching category count:", error);
        }

        // Fetch support tickets
        try {
          const supportResponse = await api.get("/support-tickets", {
            params: { limit: 1000 },
          });
          console.log("Support tickets response:", supportResponse.data);
          const tickets = supportResponse.data.data || [];
          const total = tickets.length;
          const open = tickets.filter((t) => t.status === "OPEN").length;
          const closed = tickets.filter((t) => t.status === "CLOSED").length;
          const terminated = tickets.filter((t) => t.status === "TERMINATED").length;
          const onHold = tickets.filter((t) => t.status === "ON_HOLD").length;

          setSupportStats({ total, open, closed, terminated, onHold });
          console.log("Support stats:", { total, open, closed, terminated, onHold });
        } catch (error) {
          console.error("Error fetching support stats:", error);
        }

        setCounts({
          tutors: tutorCount,
          parents: parentCount,
          students: studentCount,
          subjects: subjectCount,
          categories: categoryCount,
        });
      } catch (error) {
        console.error("Error in fetchCounts:", error);
      }
    };
    fetchCounts();
  }, []);

  useEffect(() => {
    const fetchFinancialStats = async () => {
      try {
        // Fetch financial comparison data
        const response = await getFinancialComparison();

        const payload = response?.data || {};

        // Extract expected and current buckets
        const expectedData = payload.expected || {};
        const currentData = payload.current || {};

        // Use data as-is from backend, only calculate expense if it's 0 and we have revenue and margin
        let expectedExpense = expectedData.expense || 0;
        let expectedRevenue = expectedData.revenue || 0;
        let expectedMargin = expectedData.grossMargin || 0;

        // If expense is 0 but we have revenue and margin, calculate: expense = revenue - margin
        if (expectedExpense === 0 && expectedRevenue > 0 && expectedMargin > 0) {
          console.warn("⚠️ Expense is 0, calculating from revenue - margin");
          expectedExpense = expectedRevenue - expectedMargin;
        }

        // Do same for current financials
        let currentExpense = currentData.expense || 0;
        let currentRevenue = currentData.revenue || 0;
        let currentMargin = currentData.grossMargin || 0;

        if (currentExpense === 0 && currentRevenue > 0 && currentMargin > 0) {
          currentExpense = currentRevenue - currentMargin;
        }

        console.log("💡 Final Financial Data:", {
          expected: { expense: expectedExpense, revenue: expectedRevenue, margin: expectedMargin },
          current: { expense: currentExpense, revenue: currentRevenue, margin: currentMargin },
        });

        setFinancialComparison({
          expected: {
            expense: expectedExpense,
            revenue: expectedRevenue,
            grossMargin: expectedMargin,
          },
          current: {
            expense: currentExpense,
            revenue: currentRevenue,
            grossMargin: currentMargin,
          },
        });

        console.log("💾 Financial Stats Saved to State:", {
          expected: {
            expense: expectedData.expense || 0,
            revenue: expectedData.revenue || 0,
            grossMargin: expectedData.grossMargin || 0,
          },
          current: {
            expense: currentData.expense || 0,
            revenue: currentData.revenue || 0,
            grossMargin: currentData.grossMargin || 0,
          },
        });

        // Also fetch raw Finance records to debug
        console.log("🔍 Checking raw Finance records...");
        const financeCheck = await api.get("/finance/get-all-raise-invoice", {
          params: { status: "PENDING", limit: 5 },
        });
        console.log("📋 Sample PENDING Finance Records:", financeCheck.data.data);
        if (financeCheck.data.data && financeCheck.data.data.length > 0) {
          financeCheck.data.data.forEach((record, idx) => {
            console.log(`Record ${idx + 1}:`, {
              totalAmount: record.totalAmount,
              totalTutorPay: record.totalTutorPay,
              grossMargin: record.grossMargin,
              status: record.status,
            });
          });
        }
      } catch (error) {
        console.error("❌ Error fetching financial comparison:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchFinancialStats();
  }, []);

  useEffect(() => {
    const fetchClientAnalytics = async () => {
      try {
        // Fetch all parents/clients
        const response = await api.get("/parent");
        console.log("Parent/Client analytics response:", response.data);
        const allClients = response.data.data || [];
        console.log("Total clients fetched:", allClients.length);

        // Calculate date ranges
        const now = new Date();
        const twentyEightDaysAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
        const fiftyFiveDaysAgo = new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000);

        // Filter clients by date ranges
        const clientsLast28Days = allClients.filter(
          (client) => client.createdAt && new Date(client.createdAt) >= twentyEightDaysAgo
        );
        const clientsPrevious28Days = allClients.filter(
          (client) =>
            client.createdAt &&
            new Date(client.createdAt) >= fiftyFiveDaysAgo &&
            new Date(client.createdAt) < twentyEightDaysAgo
        );

        console.log("Clients created in last 28 days:", clientsLast28Days.length);
        console.log("Clients created in previous 28 days:", clientsPrevious28Days.length);

        // Get recently created clients (sorted by creation date)
        const recentClients = [...allClients]
          .filter((client) => client.createdAt)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 4);

        console.log(
          "Recent clients to display:",
          recentClients.map((c) => ({ name: c.fullName, created: c.createdAt }))
        );

        // For enquiries, we'll use a placeholder or fetch from another endpoint if available
        // You can replace this with actual enquiries data if you have an endpoint
        const enquiriesLast28Days = 0; // Placeholder
        const enquiriesPrevious28Days = 0; // Placeholder

        setClientAnalytics({
          recentClients,
          clientsCreated28Days: clientsLast28Days.length,
          clientsCreatedPrevious28Days: clientsPrevious28Days.length,
          enquiries28Days: enquiriesLast28Days,
          enquiriesPrevious28Days: enquiriesPrevious28Days,
        });
      } catch (error) {
        console.error("Error fetching client analytics:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchClientAnalytics();
  }, []);

  useEffect(() => {
    const fetchTutorAnalytics = async () => {
      try {
        // Fetch tutors with limit of 100 (API maximum)
        const response = await api.get("/tutor", {
          params: { page: 1, limit: 100, sort: "createdAt", order: "desc" },
        });
        console.log("Tutor analytics full response:", response.data);

        // The API returns: { success, message, data: { count, tutors: [...] } }
        let allTutors = [];
        let totalCount = 0;

        if (response.data.data && response.data.data.tutors) {
          allTutors = response.data.data.tutors;
          totalCount = response.data.data.count || 0;
        } else if (response.data.tutors) {
          allTutors = response.data.tutors;
          totalCount = response.data.count || allTutors.length;
        } else if (Array.isArray(response.data.data)) {
          allTutors = response.data.data;
          totalCount = allTutors.length;
        } else if (Array.isArray(response.data)) {
          allTutors = response.data;
          totalCount = allTutors.length;
        }

        console.log("Extracted tutors count:", allTutors.length);
        console.log("Total tutors in database:", totalCount);
        console.log("Sample tutor data:", allTutors[0]);

        // Calculate date ranges
        const now = new Date();
        const twentyEightDaysAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
        const fiftyFiveDaysAgo = new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000);

        // Filter tutors by date ranges
        const tutorsLast28Days = allTutors.filter(
          (tutor) => tutor.createdAt && new Date(tutor.createdAt) >= twentyEightDaysAgo
        );
        const tutorsPrevious28Days = allTutors.filter(
          (tutor) =>
            tutor.createdAt &&
            new Date(tutor.createdAt) >= fiftyFiveDaysAgo &&
            new Date(tutor.createdAt) < twentyEightDaysAgo
        );

        console.log("Tutors created in last 28 days:", tutorsLast28Days.length);
        console.log("Tutors created in previous 28 days:", tutorsPrevious28Days.length);

        // Get recently created tutors (already sorted by createdAt desc from API)
        const recentTutors = allTutors.filter((tutor) => tutor.createdAt).slice(0, 4);

        console.log(
          "Recent tutors to display:",
          recentTutors.map((t) => ({ name: t.fullName, created: t.createdAt }))
        );

        // For interview invites, we'll use a placeholder
        const interviewInvitesLast28Days = 0; // Placeholder
        const interviewInvitesPrevious28Days = 0; // Placeholder

        setTutorAnalytics({
          recentTutors,
          tutorsCreated28Days: tutorsLast28Days.length,
          tutorsCreatedPrevious28Days: tutorsPrevious28Days.length,
          interviewInvites28Days: interviewInvitesLast28Days,
          interviewInvitesPrevious28Days: interviewInvitesPrevious28Days,
        });
      } catch (error) {
        console.error("Error fetching tutor analytics:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchTutorAnalytics();
  }, []);

  useEffect(() => {
    const fetchPaymentStats = async () => {
      try {
        // 1. Fetch pending payments from Finance (PENDING status invoices)
        const financeResponse = await api.get("/finance/get-all-raise-invoice", {
          params: {
            status: "PENDING",
            limit: 100,
            page: 1,
          },
        });
        const pendingInvoices = financeResponse.data.data || [];
        const pendingCount = pendingInvoices.length;
        const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

        // 2. Fetch expired packages/subscriptions
        const studentsResponse = await api.get("/student");
        const allStudents = studentsResponse.data.data || [];
        const now = new Date();

        // Find students with expired subscriptions
        const expiredStudents = allStudents.filter((student) => {
          if (!student.currentSubscription || !student.currentSubscription.endDate) return false;
          return new Date(student.currentSubscription.endDate) < now;
        });

        const expiredCount = expiredStudents.length;
        const expiredAmount = expiredStudents.reduce(
          (sum, s) => sum + (s.currentSubscription?.amount || 0),
          0
        );

        // 3. Calculate expected recurring payment - all active class sessions this month
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Get all active class sessions to calculate expected payments
        const classesResponse = await api.get("/classes", {
          params: {
            status: "ACTIVE",
            limit: 100,
          },
        });

        const activeClasses = classesResponse.data.data || [];
        let expectedRecurring = 0;

        // Calculate expected payment based on active sessions
        activeClasses.forEach((cls) => {
          if (cls.cycleType === "MONTHLY") {
            // For monthly cycles, add the monthly tuition fee
            expectedRecurring += cls.tuitionFee || 0;
          } else if (cls.cycleType === "WEEKLY") {
            // For weekly cycles, multiply by ~4 weeks in a month
            expectedRecurring += (cls.tuitionFee || 0) * 4;
          }
        });

        // If no active classes, fallback to pending invoices
        if (expectedRecurring === 0) {
          const allPendingResponse = await api.get("/finance/get-all-raise-invoice", {
            params: {
              status: "PENDING",
              limit: 100,
            },
          });
          const allPending = allPendingResponse.data.data || [];
          expectedRecurring = allPending.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
        }

        // 4. Calculate expected tutor salary in next 30 days (PENDING + APPROVED)
        const next30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

        // Fetch PENDING cycles
        const pendingCyclesResponse = await getAllTutorPaymentCycles({
          page: 1,
          limit: 100,
          status: "PENDING",
        });

        // Fetch APPROVED cycles
        const approvedCyclesResponse = await getAllTutorPaymentCycles({
          page: 1,
          limit: 100,
          status: "APPROVED",
        });

        const allPaymentCycles = [
          ...(pendingCyclesResponse.data || []),
          ...(approvedCyclesResponse.data || []),
        ];

        // Filter cycles within next 30 days
        const upcomingSalaries = allPaymentCycles.filter((cycle) => {
          if (!cycle.cycleEndDate) return false;
          const endDate = new Date(cycle.cycleEndDate);
          return endDate >= now && endDate <= next30Days;
        });

        const expectedSalary = upcomingSalaries.reduce(
          (sum, cycle) => sum + (cycle.calculatedAmount || 0),
          0
        );

        setPaymentStats({
          pendingPayments: { count: pendingCount, amount: pendingAmount },
          expiredPackages: { count: expiredCount, amount: expiredAmount },
          expectedRecurringPayment: expectedRecurring,
          expectedTutorSalary: expectedSalary,
        });

        console.log("Payment Stats:", {
          pendingPayments: { count: pendingCount, amount: pendingAmount },
          expiredPackages: { count: expiredCount, amount: expiredAmount },
          expectedRecurring,
          expectedSalary,
          upcomingSalariesCount: upcomingSalaries.length,
        });
      } catch (error) {
        console.error("Error fetching payment stats:", error);
      }
    };

    fetchPaymentStats();
  }, []);

  useEffect(() => {
    const fetchLeadStats = async () => {
      try {
        // Fetch all CRM entries for parents (leads)
        const crmResponse = await api.get("/crm", {
          params: {
            type: "PARENT",
            limit: 100,
            page: 1,
          },
        });

        console.log("CRM Response:", crmResponse.data);

        // API returns data.crmEntries, not data.data
        const allLeads = crmResponse.data?.data?.crmEntries || [];

        console.log("All Leads:", allLeads);
        console.log("Total Leads Count:", allLeads.length);

        if (!Array.isArray(allLeads)) {
          console.error("allLeads is not an array:", allLeads);
          return;
        }

        // Log all unique followUpStatus values to see what we're working with
        const uniqueStatuses = [...new Set(allLeads.map((lead) => lead.followUpStatus))];
        const uniqueParentStatuses = [...new Set(allLeads.map((lead) => lead.parentStatus))];
        console.log("Unique Follow-Up Statuses:", uniqueStatuses);
        console.log("Unique Parent Statuses:", uniqueParentStatuses);

        // Count leads by followUpStatus stages
        // Stage 1: Prospect - Initial enquiries
        const prospect = allLeads.filter(
          (lead) =>
            lead.followUpStatus === "Enquiry Generated" ||
            lead.followUpStatus === "Follow-up & Analysis"
        ).length;

        // Stage 2: Tutor Found - When tutor is shortlisted or found
        const tutorFound = allLeads.filter(
          (lead) =>
            lead.followUpStatus === "Tutor Shortlisting" || lead.parentStatus === "TUTOR_FINALISED"
        ).length;

        // Stage 3: Demo Fixed - Demo scheduled or completed
        const demoFixed = allLeads.filter(
          (lead) =>
            lead.followUpStatus === "Demo Fixed" ||
            lead.followUpStatus === "Demo Done" ||
            lead.followUpStatus === "Negotiation & Review"
        ).length;

        // Stage 4: Converted - Successfully converted to paying customers
        const converted = allLeads.filter(
          (lead) =>
            lead.followUpStatus === "Won" ||
            lead.followUpStatus === "PAID" ||
            lead.followUpStatus === "OTM - PAID" ||
            lead.followUpStatus === "OTM - completed" ||
            lead.parentStatus === "COMPLETED" ||
            lead.parentStatus === "ONE_TIME_PLAN"
        ).length;

        // Log detailed breakdown
        console.log("Lead Stage Breakdown:");
        console.log(
          "- Prospect:",
          prospect,
          allLeads
            .filter(
              (l) =>
                l.followUpStatus === "Enquiry Generated" ||
                l.followUpStatus === "Follow-up & Analysis"
            )
            .map((l) => l.followUpStatus)
        );
        console.log(
          "- Tutor Found:",
          tutorFound,
          allLeads
            .filter(
              (l) =>
                l.followUpStatus === "Tutor Shortlisting" || l.parentStatus === "TUTOR_FINALISED"
            )
            .map((l) => ({ followUp: l.followUpStatus, parent: l.parentStatus }))
        );
        console.log(
          "- Demo Fixed:",
          demoFixed,
          allLeads
            .filter(
              (l) =>
                l.followUpStatus === "Demo Fixed" ||
                l.followUpStatus === "Demo Done" ||
                l.followUpStatus === "Negotiation & Review"
            )
            .map((l) => l.followUpStatus)
        );
        console.log(
          "- Converted:",
          converted,
          allLeads
            .filter(
              (l) =>
                l.followUpStatus === "Won" ||
                l.followUpStatus === "PAID" ||
                l.followUpStatus === "OTM - PAID" ||
                l.followUpStatus === "OTM - completed" ||
                l.parentStatus === "COMPLETED"
            )
            .map((l) => ({ followUp: l.followUpStatus, parent: l.parentStatus }))
        );

        // Calculate conversion rate: (converted / total leads) * 100
        const totalLeads = allLeads.length;
        const conversionRate = totalLeads > 0 ? ((converted / totalLeads) * 100).toFixed(1) : 0;

        setLeadStats({
          prospect,
          tutorFound,
          demoFixed,
          converted,
          conversionRate,
        });

        console.log("Lead Stats Updated:", {
          prospect,
          tutorFound,
          demoFixed,
          converted,
          conversionRate: conversionRate + "%",
          totalLeads,
        });
      } catch (error) {
        console.error("Error fetching lead stats:", error);
        console.error("Error details:", error.response?.data);
      }
    };

    fetchLeadStats();
  }, []);

  // Calculate percentage changes
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const clientPercentageChange = calculatePercentageChange(
    clientAnalytics.clientsCreated28Days,
    clientAnalytics.clientsCreatedPrevious28Days
  );

  const enquiryPercentageChange = calculatePercentageChange(
    clientAnalytics.enquiries28Days,
    clientAnalytics.enquiriesPrevious28Days
  );

  const tutorPercentageChange = calculatePercentageChange(
    tutorAnalytics.tutorsCreated28Days,
    tutorAnalytics.tutorsCreatedPrevious28Days
  );

  const interviewPercentageChange = calculatePercentageChange(
    tutorAnalytics.interviewInvites28Days,
    tutorAnalytics.interviewInvitesPrevious28Days
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox
        // display="flex"
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
        // justifyContent="space-evenly"

        flexWrap="wrap"
        mt={3}
      >
        <Link to="/tutors" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total Tutors"
            width="100%"
            count={counts.tutors}
            icon={{ color: "info", component: <i className="material-icons">school</i> }}
            percentage={{
              color: "success",
              count: "+2%",
              text: "since last week",
            }}
          />
        </Link>
        <Link to="/parents" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total Parents"
            width="100%"
            count={counts.parents}
            icon={{
              color: "success",
              component: <i className="material-icons">group</i>,
            }}
            percentage={{
              color: "success",
              count: "+5%",
              text: "since last month",
            }}
          />
        </Link>
        <DetailedStatisticsCard
          bgColor="white"
          title="Total Students"
          count={counts.students}
          icon={{
            color: "warning",
            component: <i className="material-icons">emoji_people</i>,
          }}
          percentage={{ color: "error", count: "-1%", text: "this month" }}
        />
        <DetailedStatisticsCard
          bgColor="white"
          title="Total category"
          count={counts.categories}
          icon={{
            color: "primary",
            component: <i className="material-icons">category</i>,
          }}
          percentage={{
            color: "success",
            count: "+3%",
            text: "from last week",
          }}
        />
        <Link to="/curriculum" style={{ textDecoration: "none" }}>
          <DetailedStatisticsCard
            bgColor="white"
            title="Total subjects"
            count={counts.subjects}
            icon={{
              color: "secondary",
              component: <i className="material-icons">menu_book</i>,
            }}
            percentage={{
              color: "success",
              count: "+8%",
              text: "compared to last month",
            }}
          />
        </Link>
      </ArgonBox>

      <ArgonBox py={3}>
        <Grid container spacing={3}>
          {/* Client Analytics - Horizontal Card */}
          <Grid item xs={12} lg={6}>
            <ClientAnalyticsCard
              clientsCreated={clientAnalytics.clientsCreated28Days}
              enquiries={clientAnalytics.enquiries28Days}
              recentClients={clientAnalytics.recentClients}
              percentageChange={{
                clients: clientPercentageChange,
                enquiries: enquiryPercentageChange,
              }}
            />
          </Grid>

          {/* Tutor Analytics - Horizontal Card */}
          <Grid item xs={12} lg={6}>
            <TutorAnalyticsCard
              tutorsCreated={tutorAnalytics.tutorsCreated28Days}
              interviewInvites={tutorAnalytics.interviewInvites28Days}
              recentTutors={tutorAnalytics.recentTutors}
              percentageChange={{
                tutors: tutorPercentageChange,
                interviews: interviewPercentageChange,
              }}
            />
          </Grid>

          {/* Expected Financials */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Expected Financials"
              stats={[
                {
                  label: "Expense",
                  value: formatCurrency(financialComparison.expected.expense),
                },
                {
                  label: "Revenue",
                  value: formatCurrency(financialComparison.expected.revenue),
                },
                {
                  label: "Gross Margin",
                  value: formatCurrency(financialComparison.expected.grossMargin),
                },
              ]}
            />
          </Grid>

          {/* Current Financials */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Current Financials"
              stats={[
                {
                  label: "Expense",
                  value: formatCurrency(financialComparison.current.expense),
                },
                {
                  label: "Revenue",
                  value: formatCurrency(financialComparison.current.revenue),
                },
                {
                  label: "Gross Margin",
                  value: formatCurrency(financialComparison.current.grossMargin),
                },
              ]}
            />
          </Grid>

          {/* Tutor Section */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Tutor Section"
              stats={[
                { label: "Teachers Onboarded", value: counts.tutors.toString() },
                { label: "Revenue", value: formatCurrency(financialComparison.current.revenue) },
                {
                  label: "Gross Margin",
                  value: formatCurrency(financialComparison.current.grossMargin),
                },
              ]}
            />
          </Grid>

          {/* Customer Support - Horizontal */}
          <Grid item xs={12} md={12} lg={12}>
            <StatCard
              title="Customer Support"
              horizontal
              stats={[
                { label: "Total", value: supportStats.total.toString() },
                { label: "Open", value: supportStats.open.toString() },
                { label: "Closed", value: supportStats.closed.toString() },
                { label: "Terminated", value: supportStats.terminated.toString() },
                { label: "On Hold", value: supportStats.onHold.toString() },
              ]}
            />
          </Grid>

          {/* Payments & Finance Section */}
          <Grid item xs={12} md={12} lg={12}>
            <ArgonTypography variant="h5" fontWeight="bold" mb={2} mt={2}>
              💳 Payments & Finance
            </ArgonTypography>
          </Grid>

          {/* Pending Payments */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Pending Payments"
              stats={[
                { label: "Count", value: paymentStats.pendingPayments.count.toString() },
                { label: "Amount", value: formatCurrency(paymentStats.pendingPayments.amount) },
              ]}
            />
          </Grid>

          {/* Expired Packages */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Expired Packages"
              stats={[
                { label: "Count", value: paymentStats.expiredPackages.count.toString() },
                { label: "Amount", value: formatCurrency(paymentStats.expiredPackages.amount) },
              ]}
            />
          </Grid>

          {/* Expected Recurring Payment */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Expected Recurring (This Month)"
              stats={[
                { label: "Amount", value: formatCurrency(paymentStats.expectedRecurringPayment) },
              ]}
            />
          </Grid>

          {/* Expected Tutor Salary */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Expected Tutor Salary (30 Days)"
              stats={[{ label: "Amount", value: formatCurrency(paymentStats.expectedTutorSalary) }]}
            />
          </Grid>

          {/* Lead & CRM Management Section */}
          <Grid item xs={12} md={12} lg={12}>
            <ArgonTypography variant="h5" fontWeight="bold" mb={2} mt={2}>
              📊 Lead & CRM Management
            </ArgonTypography>
          </Grid>

          {/* Lead Funnel Stages */}
          <Grid item xs={12} md={12} lg={8}>
            <StatCard
              title="Lead Funnel Stages"
              horizontal
              stats={[
                { label: "Prospect", value: leadStats.prospect.toString() },
                { label: "Tutor Found", value: leadStats.tutorFound.toString() },
                { label: "Demo Fixed", value: leadStats.demoFixed.toString() },
                { label: "Converted", value: leadStats.converted.toString() },
              ]}
            />
          </Grid>

          {/* Conversion Analytics */}
          <Grid item xs={12} md={6} lg={4}>
            <StatCard
              title="Conversion Analytics"
              stats={[
                { label: "Lead to Client", value: leadStats.conversionRate + "%" },
                { label: "Total Converted", value: leadStats.converted.toString() },
              ]}
            />
          </Grid>
        </Grid>
      </ArgonBox>

      <ArgonBox py={3}>
        {/* <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total Tutors"
              count="328"
              icon={{ color: "info", component: <FitnessCenterIcon /> }}
              percentage={{ color: "success", count: "+55%", text: "since yesterday" }} />
              
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total parents"
              count="66"
              icon={{ color: "error", component: <i className="ni ni-world" /> }}
              percentage={{ color: "success", count: "+3%", text: "since last week" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total Students"
              count="115"
              icon={{ color: "success", component: <i className="ni ni-paper-diploma" /> }}
              percentage={{ color: "warning", count: "-2%", text: "since last quarter" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              bgColor="white"
              title="Total Categories"
              count={counts.categories}
              icon={{ color: "warning", component: <i className="ni ni-cart" /> }}
              percentage={{ color: "success", count: "+5%", text: "than last month" }}
            />
          </Grid>
          
            
        </Grid> */}

        {/* <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={12}>
            <GradientLineChart
              title="Sales Overview"
              description={
                <ArgonBox display="flex" alignItems="center">
                  <ArgonBox fontSize={size.lg} color="success" mb={0.3} mr={0.5} lineHeight={0}>
                    <Icon sx={{ fontWeight: "bold" }}>arrow_upward</Icon>
                  </ArgonBox>
                  <ArgonTypography variant="button" color="text" fontWeight="medium">
                    4% more{" "}
                    <ArgonTypography variant="button" color="text" fontWeight="regular">
                      in 2022
                    </ArgonTypography>
                  </ArgonTypography>
                </ArgonBox>
              }
              chart={gradientLineChartData}
            />
          </Grid>
          <Grid item xs={12} lg={5}>
            <Slider />
          </Grid>
        </Grid> */}
        <Grid container spacing={3}>
          {/* <Grid item xs={12} md={8}>
            <SalesTable title="Sales by Country" rows={salesTableData} />
          </Grid> */}
          {/* <Grid item xs={12} md={4}>
            <CategoriesList title="categories" categories={categoriesListData} />
          </Grid> */}
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
